// Modal "Ajustar lista pra avaliação".
// USO: organizador corrige quem realmente jogou depois da partida pra que
// o sistema de avaliação (community endorsements) apareça pra pessoas
// certas. NÃO envia msg no grupo e NÃO afeta lista pública — só grava
// direto em games_players.
//
// props: { open, gameId, gameLabel, onClose, onSaved }

import React, { useCallback, useEffect, useState } from 'react';
import { Modal, List, Button, Alert, Select, Space, Typography, Empty, message, Spin, Tag, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

const TYPE_LABEL = { '1': 'Mensalista', '2': 'Convidado', 1: 'Mensalista', 2: 'Convidado' };

export default function RosterAdjustModal({ open, gameId, gameLabel, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ playing: [], waiting: [], available: [] });
  const [pendingAdd, setPendingAdd] = useState([]);      // player_id[] a adicionar
  const [pendingRemove, setPendingRemove] = useState([]); // gp_id[] a remover (do "playing")
  const [pendingPromote, setPendingPromote] = useState([]); // gp_id[] a promover (espera → jogou)

  const load = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    try {
      const r = await api.get(`/organizer-games/${gameId}/roster-adjust`);
      setData(r.data);
      setPendingAdd([]);
      setPendingRemove([]);
      setPendingPromote([]);
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou carregar');
    } finally { setLoading(false); }
  }, [gameId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const totalChanges = pendingAdd.length + pendingRemove.length + pendingPromote.length;

  const save = async () => {
    if (totalChanges === 0) return message.info('Nenhuma alteração feita');
    setSaving(true);
    try {
      const r = await api.post(`/organizer-games/${gameId}/roster-adjust`, {
        add: pendingAdd,
        remove_gp: pendingRemove,
        promote_gp: pendingPromote,
      });
      message.success(`Aplicado: +${r.data.added} · −${r.data.removed} · ⬆ ${r.data.promoted}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      message.error(e.response?.data?.message || 'Falhou salvar');
    } finally { setSaving(false); }
  };

  // Estado visual: playing menos os marcados pra remover
  const visiblePlaying = data.playing.filter(p => !pendingRemove.includes(p.gp_id));
  const visibleWaiting = data.waiting.filter(w => !pendingPromote.includes(w.gp_id));
  const availableOptions = data.available
    .filter(p => !pendingAdd.includes(p.player_id))
    .map(p => ({
      value: p.player_id,
      label: `${p.name}${p.phone_number ? ' · ' + p.phone_number : ''}${p.type ? ' · ' + (TYPE_LABEL[p.type] || p.type) : ''}`,
    }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span>Ajustar lista {gameLabel ? <Tag style={{ marginLeft: 6 }}>{gameLabel}</Tag> : null}</span>}
      width={720}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancelar</Button>,
        <Button key="save" type="primary" loading={saving} disabled={totalChanges === 0} onClick={save}>
          Aplicar {totalChanges > 0 ? `(${totalChanges} alteraç${totalChanges > 1 ? 'ões' : 'ão'})` : ''}
        </Button>,
      ]}
    >
      <Alert
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Ajuste manual pra fins de avaliação"
        description="Nada é enviado no WhatsApp e a lista publicada no grupo não muda. Se você mexer em quem confirmou nesta partida, a próxima lista automática que subir no grupo vai refletir esses ajustes."
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : (
        <>
          {/* Adicionar */}
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              <PlusOutlined /> Adicionar jogador que jogou mas não estava na lista
            </Text>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Busca por nome ou telefone…"
              value={pendingAdd}
              onChange={setPendingAdd}
              options={availableOptions}
              optionFilterProp="label"
              showSearch
              maxTagCount="responsive"
              allowClear
              notFoundContent={data.available.length === 0 ? 'Todos os jogadores do time já estão na lista' : 'Nenhum resultado'}
            />
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Jogaram */}
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Jogaram ({visiblePlaying.length}
              {pendingRemove.length > 0 ? <Text type="secondary" style={{ marginLeft: 4 }}>— {pendingRemove.length} marcado{pendingRemove.length > 1 ? 's' : ''} pra remover</Text> : null})
            </Text>
            {visiblePlaying.length === 0 && pendingRemove.length === 0 ? (
              <Empty description="Ninguém marcado como jogou" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                bordered
                dataSource={visiblePlaying}
                style={{ maxHeight: 220, overflowY: 'auto' }}
                renderItem={p => (
                  <List.Item
                    key={p.gp_id}
                    actions={[
                      <Button
                        key="rm"
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setPendingRemove(prev => [...prev, p.gp_id])}
                        title="Remover (não jogou)"
                      />,
                    ]}
                  >
                    <Space size={8}>
                      <Text>{p.name}</Text>
                      {p.type && <Tag color={String(p.type) === '1' ? 'green' : 'blue'} style={{ fontSize: 10 }}>{TYPE_LABEL[p.type] || p.type}</Tag>}
                      {p.phone_number && <Text type="secondary" style={{ fontSize: 11 }}>{p.phone_number}</Text>}
                    </Space>
                  </List.Item>
                )}
              />
            )}
            {pendingRemove.length > 0 && (
              <Button size="small" type="link" onClick={() => setPendingRemove([])} style={{ padding: '4px 0' }}>
                Desfazer remoções
              </Button>
            )}
          </div>

          {/* Fila de espera */}
          {(data.waiting.length > 0 || pendingPromote.length > 0) && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Fila de espera ({visibleWaiting.length}
                {pendingPromote.length > 0 ? <Text type="secondary" style={{ marginLeft: 4 }}>— {pendingPromote.length} marcado{pendingPromote.length > 1 ? 's' : ''} pra promover</Text> : null})
              </Text>
              {visibleWaiting.length === 0 ? (
                <Empty description="Fila vazia" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <List
                  size="small"
                  bordered
                  dataSource={visibleWaiting}
                  style={{ maxHeight: 200, overflowY: 'auto' }}
                  renderItem={w => (
                    <List.Item
                      key={w.gp_id}
                      actions={[
                        <Button
                          key="promote"
                          type="text"
                          size="small"
                          icon={<ArrowUpOutlined />}
                          onClick={() => setPendingPromote(prev => [...prev, w.gp_id])}
                          title="Marcar como jogou"
                          style={{ color: '#128C7E' }}
                        >
                          Jogou
                        </Button>,
                      ]}
                    >
                      <Space size={8}>
                        <Text>{w.name}</Text>
                        {w.type && <Tag color={String(w.type) === '1' ? 'green' : 'blue'} style={{ fontSize: 10 }}>{TYPE_LABEL[w.type] || w.type}</Tag>}
                      </Space>
                    </List.Item>
                  )}
                />
              )}
              {pendingPromote.length > 0 && (
                <Button size="small" type="link" onClick={() => setPendingPromote([])} style={{ padding: '4px 0' }}>
                  Desfazer promoções
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
